import { useLocalStorageState } from "ahooks";
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  InfoIcon,
  XIcon,
} from "lucide-react";
import { type ReactNode, useContext } from "react";
import {
  Button,
  Disclosure,
  DisclosurePanel,
  DisclosureStateContext,
  Heading,
} from "react-aria-components";
import { tv, type VariantProps } from "tailwind-variants";

const dismissibleAlertVariants = tv({
  base: "border-1 py-2 px-4 rounded-md",
  variants: {
    color: {
      success: "bg-green-200 border-green-300 text-green-800",
      info: "bg-blue-200 border-blue-300 text-blue-800",
      warning: "bg-yellow-200 border-yellow-300 text-yellow-800",
      error: "bg-red-200 border-red-300 text-red-800",
    },
  },
  defaultVariants: {
    color: "info",
  },
});

type DismissibleAlertVariants = VariantProps<typeof dismissibleAlertVariants>;

export interface DismissibleAlertProps extends DismissibleAlertVariants {
  className?: string;
  icon?: boolean;
  collapsible?: boolean;
  title: ReactNode;
  description: ReactNode;
  dismissKey: string;
}

export function DismissibleAlert({
  icon = true,
  collapsible = false,
  title,
  description,
  dismissKey,
  ...props
}: DismissibleAlertProps) {
  const [dismissed, setDismissed] = useLocalStorageState<boolean>(
    `dismissed:${dismissKey}`,
  );

  if (dismissed !== false) return null;

  const icons: Record<string, ReactNode> = {
    info: <InfoIcon size={22} />,
    warning: <AlertTriangleIcon size={22} />,
    error: <AlertCircleIcon size={22} />,
  };

  const iconElem = icon && props.color ? icons[props.color] : null;

  return (
    <Disclosure
      isExpanded={collapsible ? undefined : true}
      defaultExpanded={!collapsible}
      className={dismissibleAlertVariants(props)}
    >
      <div className="flex items-start justify-between">
        <AlertTitle collapsible={collapsible} iconElem={iconElem}>
          {title}
        </AlertTitle>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="shrink-0 cursor-pointer rounded p-0.5 opacity-60 transition-opacity hover:opacity-100"
          aria-label="Dismiss"
        >
          <XIcon size={18} />
        </button>
      </div>
      <DisclosurePanel>
        <div className="pt-2 text-sm">{description}</div>
      </DisclosurePanel>
    </Disclosure>
  );
}

function AlertTitle({
  collapsible,
  iconElem,
  children,
}: {
  collapsible: boolean;
  iconElem: ReactNode;
  children: ReactNode;
}) {
  if (!collapsible) {
    return (
      <h3 className="flex w-full flex-row items-center gap-2 font-bold">
        {iconElem}
        <span>{children}</span>
      </h3>
    );
  }

  return (
    <Heading className="m-0 w-full font-bold">
      <Button
        slot="trigger"
        className="flex w-full cursor-default items-center gap-2 border-0 bg-transparent p-0 text-start"
      >
        {iconElem}
        <span>{children}</span>
        <Chevron />
      </Button>
    </Heading>
  );
}

function Chevron() {
  const { isExpanded } = useContext(DisclosureStateContext)!;
  return isExpanded ? (
    <ChevronDownIcon size={16} className="shrink-0" />
  ) : (
    <ChevronRightIcon size={16} className="shrink-0" />
  );
}
